import { useIntl } from 'react-intl'
import DataTable, {
	TableBody,
	TableColumn,
	TableHeader,
	TableRow,
} from 'react-md/lib/DataTables'
import { TableContainer } from 'ui/StyledComponents/Tables'
import type { LoanEventSorting } from '../types'
import { Fragment } from 'react'
import styled from 'styled-components'
import { formatDate } from 'util/formatStrings'
import FontIcon from 'react-md/lib/FontIcons'
import { loanEventCreateDialogSharegroupTableHeaders } from './constants'
import naturalSort from 'javascript-natural-sort'
import type { ApartmentSelectOptions } from '@viilu-web/shared/types/apartment'
import { useWatch } from 'react-hook-form'
import type { Control, UseFormSetValue } from 'react-hook-form'
import type { LoanEventCreateFormInputs } from './validation'
import { ErrorMessage } from 'ui/ManagerFinance/utils/components'
import type { LoanEvent } from '@viilu-web/shared/types/loans'
import { LoanEventType } from '@viilu-web/shared/enums/loans'

const LoanEventRow = styled(TableRow)<{ $filledBackground: boolean }>`
	background-color: ${(props) => (props.$filledBackground ? '#FAFAFA' : '')};
`

type Props = {
	events: LoanEvent[]
	apartmentOptions: Array<
		ApartmentSelectOptions & {
			loanShareAmount: number
		}
	>
	disabled: boolean
	control: Control<LoanEventCreateFormInputs>
	setValue: UseFormSetValue<LoanEventCreateFormInputs>
	sort: LoanEventSorting
	changeSort: (column: keyof LoanEvent, type?: string) => void
}

function LoanEventCreateDialogSharegroupTable(props: Props) {
	const {
		events,
		apartmentOptions,
		disabled,
		sort,
		control,
		setValue,
		changeSort,
	} = props
	const intl = useIntl()
	const selectedSharegroupDebtRepayments = useWatch({
		control,
		name: 'sharegroupDebtRepayments',
	})

	const onAllRowsChecked = (checked: boolean) => {
		if (checked) {
			setValue(
				'sharegroupDebtRepayments',
				events.map((e) => e.uuid),
			)
		} else {
			setValue('sharegroupDebtRepayments', [])
		}
	}

	const renderTableHeaders = () => {
		return (
			<TableRow>
				{loanEventCreateDialogSharegroupTableHeaders.map((header) => (
					<TableColumn
						key={header.key}
						onClick={() => changeSort(header.key as keyof LoanEvent)}
					>
						<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
							<p className="text-strong text-subtle">{header.title(intl)}</p>
							{sort.column === header.key && (
								<FontIcon style={{ fontSize: 12 }}>
									{sort.order === 'asc' ? 'arrow_upward' : 'arrow_downward'}
								</FontIcon>
							)}
						</div>
					</TableColumn>
				))}
			</TableRow>
		)
	}

	const renderTableRows = () => {
		return events
			.sort((a, b) => {
				const aValue = a[sort.column]
				const bValue = b[sort.column]
				return sort.order === 'asc'
					? naturalSort(aValue, bValue)
					: naturalSort(bValue, aValue)
			})
			.map((event, index) => {
				const correspondingApartmentOption = apartmentOptions.find(
					(apt) => apt.value === event.shareGroupUUID,
				)

				const amountColumnValue =
					event.type === LoanEventType.SHAREGROUP_DEBT_REPAYMENT
						? event.adjustedAmount
						: event.amount
				return (
					<Fragment key={event.uuid}>
						<LoanEventRow
							onCheckboxClick={() => {
								const currentSelection = selectedSharegroupDebtRepayments || []
								let newSelection: string[]
								if (currentSelection.includes(event.uuid)) {
									newSelection = currentSelection.filter(
										(selection) => selection !== event.uuid,
									)
								} else {
									newSelection = [...currentSelection, event.uuid]
								}
								setValue('sharegroupDebtRepayments', newSelection)
							}}
							selected={selectedSharegroupDebtRepayments?.includes(event.uuid)}
							$filledBackground={index % 2 === 0}
						>
							<TableColumn>{formatDate(event.eventDate)}</TableColumn>
							<TableColumn>{formatDate(event.registerDate)}</TableColumn>
							<TableColumn>{correspondingApartmentOption?.label}</TableColumn>
							<TableColumn>
								{correspondingApartmentOption?.loanShareAmount}
							</TableColumn>
							<TableColumn>{amountColumnValue}</TableColumn>
							<TableColumn>{event?.units || 0}</TableColumn>
						</LoanEventRow>
					</Fragment>
				)
			})
			.concat(
				<tr
					key="empty-row"
					className="full-width"
					style={{ height: 100, background: 'white' }}
				></tr>,
			)
	}

	return (
		<>
			{events.length > 0 ? (
				<TableContainer>
					<DataTable
						baseId="loan-events-table"
						className="md-paper--1"
						plain={disabled}
						onRowToggle={
							disabled
								? undefined
								: (rowIdx, checked) => {
										rowIdx === 0 && onAllRowsChecked(checked)
								  }
						}
						style={{
							background: 'white',
							opacity: disabled ? 0.6 : 1,
						}}
					>
						<TableHeader
							style={{
								position: 'sticky',
								top: 0,
								zIndex: 1,
								background: 'white',
								borderBottom: 'rgba(0, 0, 0, 0.12) solid 2px',
							}}
						>
							{renderTableHeaders()}
						</TableHeader>
						<TableBody>{renderTableRows()}</TableBody>
					</DataTable>
				</TableContainer>
			) : (
				<ErrorMessage>
					{intl.formatMessage({
						defaultMessage:
							'Käytettäviä osakeryhmien lyhennyksiä ei löytynyt. Joko osakeryhmien lyhennykset on jo käytetty lainalyhennyksessä tai niitä ei ole vielä luotu. Luo osakkaan velkaosuus suorituksia jatkaaksesi eteenpäin.',
						description:
							'No usable share group repayments were found. Either the share group repayments have already been used in a loan repayment, or they have not been created yet. Create sharegroup debt share payments to continue.',
					})}
				</ErrorMessage>
			)}
		</>
	)
}

export default LoanEventCreateDialogSharegroupTable
